import { jsx } from 'panel';
import kebabCase from 'lodash/kebabCase';
import { PropertyType, ResourceType } from 'common/report/constants';
import { RowNumericAggregator, RowNumericProperty, RowNumericSubAggregator } from 'common/widgets/query-entry-section/components/aggregations';
import { BehaviorRowDateRange } from 'common/widgets/query-entry-section/components/behaviors';
import { RowFilters } from 'common/widgets/query-entry-section/components/filters';
import { RowSelector } from 'common/widgets/query-entry-section/components/selector';
import { SelectEl } from 'common/components/select';
import { EntryPart } from 'common/widgets/query-entry-section/types';
import { StepRangeSelectorEl } from 'common/widgets/step-range-selector';
import { hasNewQBUI } from 'common/widgets/query-builder/util';
import { FilterValueTemplate, getFilterOperatorSymbol, getFilterOperatorTooltip } from '../filter/utils';
import { BaseEntryRowEl } from '../base/template';
import { isBehavioralFilterNegated } from '../behavior/utils';
const NO_OPERATOR_TYPES = new Set([PropertyType.Object, PropertyType.NonProperty]);
/**
 * QuerySuperEntryRow is a catch-all renderer for all type of rows
 */

export function SuperEntryRowEl(_ref) {
  let {
    section,
    entry,
    index
  } = _ref;

  /** @todo [pablo] consider displaying a disabled tooltip for these conditions instead of removing them from the UI */
  const displayEntryFilter = this.entryFilter && !section.helpers.hasFirstTimeEventFilter(index) && !isBehavioralFilterNegated(entry);
  const displayEntryAggregations = section.attr("metrics-mode") && (this.entryFilter ? !section.helpers.hasFirstTimeEventFilter(index) : true);
  const exclusionStepEl = this.hasEventValue() && section.helpers.isExclusionStepsSection() && jsx(StepRangeSelectorEl, {
    attrs: {
      selected: JSON.stringify(section.helpers.exclusionSteps.getExclusionStep(index)),
      'num-steps': section.helpers.exclusionSteps.getNumExclusionSteps(),
      theme: "select",
      label: !hasNewQBUI() && "between"
    },
    on: {
      select: ev => section.helpers.exclusionSteps.onExclusionStepSelected(ev, index)
    }
  });

  const selectorRenderer = () => {
    var _a;

    return jsx(null, null, jsx(RowSelector, {
      el: section,
      entry: entry,
      index: index,
      isPlaceholder: !this.hasEventValue()
    }), !hasNewQBUI() && exclusionStepEl, displayEntryAggregations && entry.type !== ResourceType.Cohort && (this.entryFilter ? displayEntryFilter : true) && jsx(RowNumericAggregator, {
      el: section,
      entry: entry,
      index: index
    }), section.helpers.canPerformPropertyAggregation(entry) && jsx(RowNumericProperty, {
      el: section,
      entry: entry,
      index: index,
      isPlaceholder: !this.hasPropertyValue()
    }), displayEntryFilter && !NO_OPERATOR_TYPES.has(this.entryFilter.propertyType) && jsx(SelectEl, {
      attrs: {
        elref: "filterOperator",
        'menu-width': "200",
        'menu-height': "400",
        'selected-option': getFilterOperatorSymbol(this.entryFilter),
        theme: "select",
        tooltip: getFilterOperatorTooltip(this.entryFilter)
      },
      class: {
        ["for-".concat(this.entryFilter.propertyType)]: true,
        ["for-".concat(kebabCase(this.entryFilter.filterOperator), "-operator")]: true
      },
      props: {
        sections: this.getEntryFilterOperators()
      },
      on: {
        change: ev => this.handleEntryFilterOperatorUpdate(ev)
      }
    }), displayEntryFilter && FilterValueTemplate[this.entryFilter.propertyType] && FilterValueTemplate[this.entryFilter.propertyType]({
      index: this.index,
      activeEntry: this.section.state.activeEntry,
      entryPart: this.section.state.entryPart,
      filter: this.entryFilter,
      handleFilterValueChange: filterValue => this.handleEntryFilterValueUpdate("filterValue", filterValue),
      onSelectMenuChange: (ev, index, entryPart) => this.section.helpers.onSelectMenuChange(ev, index, entryPart),
      setActiveEntry: (state, index, part) => this.section.helpers.setActiveEntry(state, index, part),
      updateEntry: (filter, trackingProps) => this.updateEntryFilter(filter, trackingProps),
      eventNames: this.hasEventValue() ? [(_a = this.entry.event) === null || _a === void 0 ? void 0 : _a.value] : [],
      projectId: this.section.attr("project-id"),
      workspaceId: this.section.attr("workspace-id")
    }), section.helpers.isDateRangeType(entry.type) && jsx(BehaviorRowDateRange, {
      el: section,
      entry: entry,
      index: index
    }), section.attr("metrics-mode") && entry.type !== ResourceType.Cohort && jsx(RowNumericSubAggregator, {
      el: section,
      entry: entry,
      index: index
    }));
  };

  const bodyRowRenderer = () => jsx(null, null, jsx("div", {
    sel: '.mp-query-entry-row.for-filters'
  }, jsx(RowFilters, {
    el: section,
    entry: entry,
    index: index,
    source: EntryPart.Filters,
    handleUpdate: ev => this.handleFilterSectionUpdate(ev, EntryPart.Filters)
  }), jsx(RowFilters, {
    el: section,
    entry: entry,
    index: index,
    source: EntryPart.BehavioralFilters,
    handleUpdate: ev => this.handleFilterSectionUpdate(ev, EntryPart.BehavioralFilters)
  })), hasNewQBUI() && exclusionStepEl);

  return jsx(BaseEntryRowEl, {
    index: index,
    el: section,
    entry: entry,
    rowRenderer: {
      prefixRenderer: null,
      selectorRenderer,
      bodyRowRenderer,
      suffixRenderer: null
    },
    isPlaceholder: this.isPlaceholder()
  });
}